import clsx from "clsx"
import { useEffect, useRef, useState } from "react"

import { type SX, theme } from "../../theme"
import styles from "./Video.module.css"

export interface VideoProps {
  src: string
  loop?: boolean
  autoPlay?: boolean
  sizing?: "contain" | "cover" | "none"
  borderRadius?: 0 | 4
  boxShadow?: "3"
  animateOnLoad?: boolean
  /**
   * Whether to show the playback controls. Defaults to true.
   */
  withControls?: boolean
  preload?: "none" | "metadata" | "auto"
  poster?: string
  sx?: SX
  onLoad?: () => void
}

/*
 * A Basic Video that can play an enter animation when it loads.
 */
export function Video(props: VideoProps) {
  const {
    src,
    onLoad,
    sizing = "none",
    borderRadius = 0,
    boxShadow,
    withControls = true,
    animateOnLoad = true,
    sx,
    ...otherProps
  } = props

  const videoRef = useRef<HTMLVideoElement>(null)
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    videoRef.current?.load()
  }, [src])

  function getLoadingState() {
    if (!animateOnLoad) return undefined
    if (isLoaded) return "loaded"
    return "loading"
  }

  const loadingState = getLoadingState()

  function onVideoLoad() {
    setIsLoaded(true)
    onLoad?.()
  }

  return (
    <video
      {...otherProps}
      ref={videoRef}
      controls={withControls}
      className={clsx(
        styles[`sizing-${sizing}`],
        styles[`borderRadius-${borderRadius}`],
        loadingState && styles[loadingState],
      )}
      style={{ ...sx, boxShadow: boxShadow && theme.boxShadow[boxShadow] }}
      onLoadedData={onVideoLoad}
    >
      <source src={src} type="video/mp4" />
      <track kind="captions" />
    </video>
  )
}
